import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  Table2,
  GanttChart,
  Kanban,
  Calendar,
  MoreHorizontal,
  Settings,
  Users,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useProjectQuery,
  useProjectMembersQuery,
  useAddProjectMember,
  useRemoveProjectMember,
} from '@/hooks/useProjects';
import { useSheetsQuery, useCreateSheet, useDeleteSheet } from '@/hooks/useSheets';
import { useCollaboration } from '@/hooks/useCollaboration';
import { PresenceAvatars } from '@/components/collaboration/PresenceAvatars';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/hooks/useAuth';

const SHEET_TYPE_ICONS: Record<string, any> = {
  grid: Table2,
  gantt: GanttChart,
  card: Kanban,
  calendar: Calendar,
};

export function ProjectView() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [showCreateSheet, setShowCreateSheet] = useState(false);
  const [showMembersDialog, setShowMembersDialog] = useState(false);
  const [newSheetName, setNewSheetName] = useState('');
  const [newMemberUserId, setNewMemberUserId] = useState('');
  const [newMemberRole, setNewMemberRole] = useState<'owner' | 'admin' | 'member' | 'viewer'>('member');
  const [membersError, setMembersError] = useState<string | null>(null);
  const { user } = useAuth();

  const { data: project, isLoading: projectLoading, error: projectError } = useProjectQuery(projectId || '');
  const { data: members = [], isLoading: membersLoading } = useProjectMembersQuery(projectId || '');
  const addMemberMutation = useAddProjectMember(projectId || '');
  const removeMemberMutation = useRemoveProjectMember(projectId || '');
  const { data: sheetsData, isLoading: sheetsLoading } = useSheetsQuery(projectId || '');
  const createSheet = useCreateSheet(projectId || '');
  const deleteSheet = useDeleteSheet(projectId || '');
  const { users } = useCollaboration(projectId);

  // Handle both array and paginated response
  const sheets = Array.isArray(sheetsData) ? sheetsData : (sheetsData as any)?.sheets || sheetsData || [];

  const isLoading = projectLoading || sheetsLoading;

  const handleCreateSheet = async () => {
    if (!newSheetName.trim() || !projectId) return;
    try {
      const sheet = await createSheet.mutateAsync({
        name: newSheetName.trim(),
        sheetType: 'grid',
      });
      setShowCreateSheet(false);
      setNewSheetName('');
      if (sheet?.id) {
        navigate(`/projects/${projectId}/sheets/${sheet.id}`);
      }
    } catch (err) {
      console.error('Failed to create sheet:', err);
    }
  };

  const handleDeleteSheet = async (sheetId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (confirm('Are you sure you want to delete this sheet?')) {
      try {
        await deleteSheet.mutateAsync(sheetId);
      } catch (err: any) {
        const status = err?.response?.status;
        const serverMessage = err?.response?.data?.message;
        let message = serverMessage || err?.message || 'Failed to delete sheet';
        if (status === 403 && !serverMessage) {
          message = 'You do not have permission to delete this sheet. Contact the project owner.';
        }
        toast.error(message);
      }
    }
  };

  const handleAddMember = async () => {
    if (!projectId || !newMemberUserId.trim()) return;
    setMembersError(null);

    try {
      await addMemberMutation.mutateAsync({
        userId: newMemberUserId.trim(),
        role: newMemberRole,
      });
      setNewMemberUserId('');
      setNewMemberRole('member');
    } catch (err: any) {
      const message = err?.response?.data?.message;
      setMembersError(Array.isArray(message) ? message.join(', ') : message || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (memberUserId: string) => {
    if (!projectId) return;
    setMembersError(null);

    try {
      await removeMemberMutation.mutateAsync(memberUserId);
    } catch (err: any) {
      const message = err?.response?.data?.message;
      setMembersError(Array.isArray(message) ? message.join(', ') : message || 'Failed to remove member');
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex items-center gap-2 text-muted-foreground">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <span>Loading project...</span>
        </div>
      </div>
    );
  }

  if (projectError || !project) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate('/projects')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Projects
        </Button>
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <h2 className="text-lg font-semibold text-destructive">Error loading project</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {projectError instanceof Error ? projectError.message : 'Project not found'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/projects')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{project.name}</h1>
            <Badge variant={project.status === 'active' ? 'default' : 'secondary'}>
              {project.status}
            </Badge>
            {users.length > 0 && <PresenceAvatars users={users} />}
          </div>
          {project.description && (
            <p className="mt-1 text-muted-foreground">{project.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowMembersDialog(true)}>
            <Users className="h-4 w-4 mr-2" />
            Members
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/settings')}>
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
        </div>
      </div>

      {/* Create Sheet Modal */}
      {showCreateSheet && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-background rounded-xl shadow-2xl border p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold mb-4">Create New Sheet</h2>
            <Input
              placeholder="Sheet name"
              value={newSheetName}
              onChange={(e) => setNewSheetName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateSheet()}
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => { setShowCreateSheet(false); setNewSheetName(''); }}>
                Cancel
              </Button>
              <Button onClick={handleCreateSheet} disabled={!newSheetName.trim() || createSheet.isPending}>
                {createSheet.isPending ? 'Creating...' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={showMembersDialog} onOpenChange={setShowMembersDialog}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Project Members</DialogTitle>
            <DialogDescription>
              Invite by user ID and manage existing project access.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="grid gap-3 md:grid-cols-[1fr_180px_auto] items-end">
              <div className="space-y-2">
                <Label htmlFor="member-user-id">User ID</Label>
                <Input
                  id="member-user-id"
                  placeholder="Enter user UUID"
                  value={newMemberUserId}
                  onChange={(e) => setNewMemberUserId(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="member-role">Role</Label>
                <Select
                  value={newMemberRole}
                  onValueChange={(value) => setNewMemberRole(value as 'owner' | 'admin' | 'member' | 'viewer')}
                >
                  <SelectTrigger id="member-role">
                    <SelectValue placeholder="Role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owner">Owner</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={handleAddMember}
                disabled={!newMemberUserId.trim() || addMemberMutation.isPending}
              >
                {addMemberMutation.isPending ? 'Adding...' : 'Add Member'}
              </Button>
            </div>

            {membersError && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {membersError}
              </div>
            )}

            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Current Members</h3>
              {membersLoading ? (
                <p className="text-sm text-muted-foreground">Loading members...</p>
              ) : members.length === 0 ? (
                <p className="text-sm text-muted-foreground">No members found for this project.</p>
              ) : (
                <div className="max-h-72 overflow-auto rounded-md border">
                  {members.map((member) => {
                    const displayName =
                      member.user.displayName ||
                      [member.user.firstName, member.user.lastName].filter(Boolean).join(' ').trim() ||
                      member.user.email;

                    return (
                      <div key={member.userId} className="flex items-center justify-between gap-3 border-b px-3 py-2 last:border-b-0">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{displayName}</p>
                          <p className="truncate text-xs text-muted-foreground">{member.user.email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="capitalize">
                            {member.role}
                          </Badge>
                          {member.canShare && <Badge variant="secondary">Can Share</Badge>}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleRemoveMember(member.userId)}
                            disabled={removeMemberMutation.isPending || member.userId === user?.id}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMembersDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sheets Section */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Sheets</h2>
          <Button size="sm" onClick={() => setShowCreateSheet(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Sheet
          </Button>
        </div>

        {sheets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center border rounded-lg bg-muted/30">
            <Table2 className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold">No sheets yet</h3>
            <p className="mt-2 text-muted-foreground max-w-md">
              Create a sheet to start adding tasks and managing your project.
            </p>
            <Button className="mt-4" size="sm" onClick={() => setShowCreateSheet(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Sheet
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {sheets.map((sheet: any) => {
              const Icon = SHEET_TYPE_ICONS[sheet.type || sheet.sheetType] || Table2;
              return (
                <Link key={sheet.id} to={`/projects/${projectId}/sheets/${sheet.id}`}>
                  <Card className="h-full transition-shadow hover:shadow-md cursor-pointer group">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <Icon className="h-5 w-5 text-primary" />
                          <CardTitle className="text-base">{sheet.name}</CardTitle>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.preventDefault()}>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.preventDefault(); navigate(`/projects/${projectId}/sheets/${sheet.id}`); }}>
                              <Settings className="h-4 w-4 mr-2" />
                              Open
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={(e) => handleDeleteSheet(sheet.id, e)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      {sheet.description && (
                        <CardDescription className="line-clamp-2">
                          {sheet.description}
                        </CardDescription>
                      )}
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <Badge variant="outline" className="text-xs">
                          {sheet.type || sheet.sheetType}
                        </Badge>
                        {sheet._count && (
                          <span>{sheet._count.tasks} tasks</span>
                        )}
                        {sheet.isDefault && (
                          <Badge variant="secondary" className="text-xs">Default</Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default ProjectView;
